import { IonChip, IonCol, IonNote, IonRow, IonSkeletonText } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { debug } from "../../App";
import ApiService, { fetcher } from "../../services/ApiService";
import InputFactory from "../Factories/Input/InputFactory";

interface FormParams {
  name: string;
  queryParameters?: { [key: string]: any };
  disabled?: boolean;
  includeLabels?: boolean;
  partialEditMode?: boolean;
  formData?: { [key: string]: any };
  onChange?: (key: string, value: any) => void;
  onSubmitting?: (tab?: Tab) => void;
  onSuccess: (response: any, formData?: any, tab?: Tab) => void;
  onError: (err: any) => void;
}

export interface Question {
  id: string;
  name: string;
  key: string;
  attr: string;
  internal_name: string;
  type: string;
  rules: string[];
}

export interface Tab {
  id: string;
  name: string;
  questions: Question[];
}

export interface Form {
  id: string;
  name: string;
  handler: string;
  tabs: Tab[];
}

export interface FormComponentRef {
  submit: () => void;
}

export const FormComponent = forwardRef<FormComponentRef, FormParams>((props: FormParams, ref) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>(props.formData || {});
  const [errors, setErrors] = useState<any>();
  const [activeTab, setActiveTab] = useState<string>();
  const [filteredTabs, setFilteredTabs] = useState<Tab[]>([]);

  const { isLoading, data } = useQuery({
    queryKey: ["form", props.name],
    queryFn: () => {
      const queryParams = new URLSearchParams(props.queryParameters).toString();
      return fetcher<Form>(`form/${props.name}?${queryParams}`);
    },
  });

  useEffect(() => {
    if (data && data.tabs) {
      if (data.tabs) {
        setFilteredTabs(
          data.tabs.filter((tab: Tab) => {
            return tab.questions?.length > 0;
          }),
        );

        setActiveTab(data.tabs[0].id);
      }
    }
  }, [data]);

  const handleChange = (e: any) => {
    const key = e.detail.name ? e.detail.name : e.target.name;
    const newFormData = { ...formData };

    if (e.detail.value !== undefined) {
      newFormData[key] = e.detail.value;
    } else {
      delete newFormData[key];
    }

    debug("Handling a change to " + key + ", new value", e.detail.value);

    setFormData(newFormData);
    debug("formData updated to", newFormData);
    props.onChange?.(key, e.detail.value);
  };

  const renderInput = (question: Question) => {
    const { key: name, name: label, attr, rules, ...options } = question;
    const formattedLabel = label + (rules.includes("required") ? " *" : "");

    return (
      <div key={`input-question-${question.id}`} className={`input__container input__container--type-${question.type} ${errors?.[name] && "has-error"}`}>
        <InputFactory
          {...options}
          name={name}
          value={formData[name]}
          label={props.includeLabels ? formattedLabel : false}
          placeholder={props.includeLabels ? "" : formattedLabel}
          onIonChange={handleChange}
          className={"input__input input-factory__input input-factory__input--type-" + question.type + " " + (props.includeLabels ? "input-factory__input--has-label" : "")}
          attr={question.attr}
          error={errors?.[name]}
          disabled={props.disabled || false}
        />
        {renderInputError(question)}
      </div>
    );
  };

  const handleSubmit = useCallback(() => {
    if (!data) {
      return;
    }

    const tab = data.tabs.find((tab: Tab) => tab.id === activeTab);
    props.onSubmitting?.(tab);

    const req = new FormData();
    Object.keys(formData).forEach((key) => {
      let value: string = "";

      debug("key = ", key, "formData[key] = ", formData[key], "type = ", typeof formData[key]);

      if (typeof formData[key] === "string" || typeof formData[key] === "number") {
        value = String(formData[key]).toString();
      } else if (formData[key] === null || formData[key] === undefined) {
        value = "";
      } else if (typeof formData[key] === "object") {
        value = JSON.stringify(formData[key]);
      }

      if (value !== null && value !== undefined) {
        req.set(key, value);
      }
    });
    const reqOptions = { headers: { "Content-Type": false } };

    ApiService.post(data.handler + "/" + activeTab + "?" + new URLSearchParams(props.queryParameters).toString(), req, reqOptions)
      .then((resp) => {
        props.onSuccess(resp, formData, tab);
      })
      .catch((err) => {
        setErrors(err.errors);
        props.onError(err);
      });
  }, [data, formData, props, activeTab]);

  const renderInputError = (question: Question) => {
    return (
      errors &&
      errors[question.key] &&
      errors[question.key].map((err: string) => (
        <IonNote key={err} color="danger" className={"block"}>
          {err}
        </IonNote>
      ))
    );
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        submit: handleSubmit,
      };
    },
    [handleSubmit],
  );

  return (
    <div className={"form-component"}>
      {isLoading ? (
        <div className={"register__inputs"}>
          <IonSkeletonText animated style={{ width: "30%", height: "24px" }} />
          <IonSkeletonText animated style={{ width: "100%", height: "48px", marginBottom: 24 }} />

          <IonSkeletonText animated style={{ width: "30%", height: "24px" }} />
          <IonSkeletonText animated style={{ width: "100%", height: "48px", marginBottom: 24 }} />

          <IonSkeletonText animated style={{ width: "30%", height: "24px" }} />
          <IonSkeletonText animated style={{ width: "100%", height: "48px", marginBottom: 24 }} />

          <IonSkeletonText animated style={{ width: "30%", height: "24px" }} />
          <IonSkeletonText animated style={{ width: "100%", height: "48px" }} />
        </div>
      ) : (
        data && (
          <div className={"form-component__form"}>
            {filteredTabs.length > 1 && (
              <IonRow className={"form-component__tabs"}>
                {filteredTabs.map((tab: Tab) => (
                  <IonCol key={"tab_" + tab.id} className={"ion-no-padding"}>
                    <IonChip
                      onClick={() => {
                        setActiveTab(tab.id);
                      }}
                      key={"tab_chip_" + tab.id}
                      color={activeTab === tab.id ? "dark" : "medium"}
                    >
                      {tab.name}
                    </IonChip>
                  </IonCol>
                ))}
              </IonRow>
            )}

            {filteredTabs.map((tab: Tab) => {
              return (
                <div
                  className={"form__tab form__tab__" + tab.id}
                  key={"tab_" + tab.id}
                  style={{
                    display: activeTab === tab.id ? "block" : "none",
                  }}
                >
                  <div className="register__inputs">{tab.questions?.filter((question: Question) => question).map(renderInput)}</div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
});
